import React, { useState, useEffect, useRef } from "react";
import {
  User,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  Calendar,
  Edit2,
  Save,
  Plus,
  Trash2,
  Building,
  CheckCircle,
  XCircle,
  Camera,
} from "lucide-react";

const ProfilePage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const fileInputRef = useRef(null);
  const [profileImage, setProfileImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    full_name: "",
    mobile_number: "",
    location: "",
    title: "",
    professional_info: {
      total_experience: "",
      current_company: "",
      current_role: "",
      preferred_location: "",
    },
    skills: [],
    experience: [],
    education: [],
    certificates: [],
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(
        "https://api.made2tech.com/sub-collection/user-details?sub_collection_name=techform",
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch profile");

      const data = await response.json();
      setFormData(data.data);

      if (data.data.profile_photo) {
        setPreviewUrl(data.data.profile_photo);
      }
    } catch (err) {
      setError("Failed to load profile data");
      console.error("Error fetching profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProfessionalInfoChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      professional_info: {
        ...prev.professional_info,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const formDataToSend = new FormData();

      if (profileImage) {
        formDataToSend.append("file", profileImage);
      }

      formDataToSend.append("update_data", JSON.stringify(formData));

      const response = await fetch(
        "https://api.made2tech.com/sub-collection/update-user?sub_collection_name=techform",
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: formDataToSend,
        }
      );

      if (!response.ok) throw new Error("Failed to update profile");

      setSuccessMessage("Profile updated successfully!");
      setIsEditing(false);
      await fetchUserProfile();

      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      setError("Failed to update profile");
      console.error("Error updating profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const addSkill = (skill) => {
    if (skill.trim()) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, skill.trim()],
      }));
    }
  };

  const removeSkill = (index) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index),
    }));
  };

  const addExperience = () => {
    setFormData((prev) => ({
      ...prev,
      experience: [
        ...prev.experience,
        {
          role: "",
          company_name: "",
          location: "",
          duration: "",
          responsibilities: [],
        },
      ],
    }));
  };

  const updateExperience = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      experience: prev.experience.map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp
      ),
    }));
  };

  const removeExperience = (index) => {
    setFormData((prev) => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index),
    }));
  };

  const addEducation = () => {
    setFormData((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        {
          degree_name: "",
          institute_name: "",
          year: "",
          location: "",
          gpa: "",
        },
      ],
    }));
  };

  const updateEducation = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      education: prev.education.map((edu, i) =>
        i === index ? { ...edu, [field]: value } : edu
      ),
    }));
  };

  const removeEducation = (index) => {
    setFormData((prev) => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  };

  const addCertificate = () => {
    setFormData((prev) => ({
      ...prev,
      certificates: [
        ...prev.certificates,
        {
          certificate_name: "",
          issuer: "",
          year: "",
        },
      ],
    }));
  };

  const updateCertificate = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      certificates: prev.certificates.map((cert, i) =>
        i === index ? { ...cert, [field]: value } : cert
      ),
    }));
  };

  const removeCertificate = (index) => {
    setFormData((prev) => ({
      ...prev,
      certificates: prev.certificates.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#24786D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
              <XCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              {successMessage}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Profile Header */}
            <div className="relative h-48 bg-gradient-to-r from-[#24786D] to-[#1a5a52]">
              <div className="absolute -bottom-16 left-8">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <User className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                    >
                      <Camera className="w-5 h-5 text-[#24786D]" />
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div className="pt-20 px-8 pb-8">
              {/* Edit/Save Button */}
              <div className="flex justify-end mb-6">
                {isEditing ? (
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-[#24786D] text-white px-4 py-2 rounded-lg hover:bg-[#1a5a52] transition-colors"
                  >
                    <Save className="w-5 h-5" />
                    Save Changes
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsEditing(true);
                    }}
                    className="flex items-center gap-2 bg-[#24786D] text-white px-4 py-2 rounded-lg hover:bg-[#1a5a52] transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                    Edit Profile
                  </button>
                )}
              </div>

              {/* Basic Information */}
              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#24786D] mb-4">
                  Basic Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 mr-2" />
                      Username
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) =>
                        handleInputChange("username", e.target.value)
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 mr-2" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) =>
                        handleInputChange("full_name", e.target.value)
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <Phone className="w-4 h-4 mr-2" />
                      Mobile Number
                    </label>
                    <input
                      type="text"
                      value={formData.mobile_number}
                      onChange={(e) =>
                        handleInputChange("mobile_number", e.target.value)
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <MapPin className="w-4 h-4 mr-2" />
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <Briefcase className="w-4 h-4 mr-2" />
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#24786D] mb-4">
                  Professional Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <Calendar className="w-4 h-4 mr-2" />
                      Total Experience
                    </label>
                    <input
                      type="text"
                      value={formData.professional_info.total_experience}
                      onChange={(e) =>
                        handleProfessionalInfoChange(
                          "total_experience",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <Building className="w-4 h-4 mr-2" />
                      Current Company
                    </label>
                    <input
                      type="text"
                      value={formData.professional_info.current_company}
                      onChange={(e) =>
                        handleProfessionalInfoChange(
                          "current_company",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <Briefcase className="w-4 h-4 mr-2" />
                      Current Role
                    </label>
                    <input
                      type="text"
                      value={formData.professional_info.current_role}
                      onChange={(e) =>
                        handleProfessionalInfoChange(
                          "current_role",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                  <div className="relative">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <MapPin className="w-4 h-4 mr-2" />
                      Preferred Location
                    </label>
                    <input
                      type="text"
                      value={formData.professional_info.preferred_location}
                      onChange={(e) =>
                        handleProfessionalInfoChange(
                          "preferred_location",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg bg-white disabled:bg-gray-50 border-gray-200 focus:border-[#24786D] focus:ring-2 focus:ring-[#24786D]"
                    />
                  </div>
                </div>
              </div>

              {/* Skills Section */}
              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#24786D] mb-4">
                  Skills
                </h2>
                {isEditing && (
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      placeholder="Add a new skill"
                      className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-[#24786D]"
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          addSkill(e.target.value.trim());
                          e.target.value = "";
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.querySelector(
                          'input[placeholder="Add a new skill"]'
                        );
                        if (input.value.trim()) {
                          addSkill(input.value.trim());
                          input.value = "";
                        }
                      }}
                      className="bg-[#24786D] text-white p-2 rounded-lg hover:bg-[#1a5a52] transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map((skill, index) => (
                    <div
                      key={index}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full flex items-center gap-2"
                    >
                      <span>{skill}</span>
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => removeSkill(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Experience Section */}
              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#24786D] mb-4">
                  Experience
                </h2>
                {isEditing && (
                  <button
                    type="button"
                    onClick={addExperience}
                    className="mb-4 flex items-center gap-2 text-[#24786D] hover:text-[#1a5a52] transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Experience
                  </button>
                )}
                {formData.experience.map((exp, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 mb-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-4">
                        <input
                          type="text"
                          value={exp.role}
                          onChange={(e) =>
                            updateExperience(index, "role", e.target.value)
                          }
                          disabled={!isEditing}
                          className="w-full text-xl font-semibold text-gray-800 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                          placeholder="Role"
                        />
                        <input
                          type="text"
                          value={exp.company_name}
                          onChange={(e) =>
                            updateExperience(
                              index,
                              "company_name",
                              e.target.value
                            )
                          }
                          disabled={!isEditing}
                          className="w-full text-[#24786D] border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                          placeholder="Company Name"
                        />
                        <input
                          type="text"
                          value={exp.location}
                          onChange={(e) =>
                            updateExperience(index, "location", e.target.value)
                          }
                          disabled={!isEditing}
                          className="w-full text-gray-600 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                          placeholder="Location"
                        />
                        <input
                          type="text"
                          value={exp.duration}
                          onChange={(e) =>
                            updateExperience(index, "duration", e.target.value)
                          }
                          disabled={!isEditing}
                          className="w-full text-gray-600 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                          placeholder="Duration"
                        />
                      </div>
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => removeExperience(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Education Section */}
              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#24786D] mb-4">
                  Education
                </h2>
                {isEditing && (
                  <button
                    type="button"
                    onClick={addEducation}
                    className="mb-4 flex items-center gap-2 text-[#24786D] hover:text-[#1a5a52] transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Education
                  </button>
                )}
                {formData.education.map((edu, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 mb-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-4">
                        <input
                          type="text"
                          value={edu.degree_name}
                          onChange={(e) =>
                            updateEducation(
                              index,
                              "degree_name",
                              e.target.value
                            )
                          }
                          disabled={!isEditing}
                          className="w-full text-xl font-semibold text-gray-800 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                          placeholder="Degree Name"
                        />
                        <input
                          type="text"
                          value={edu.institute_name}
                          onChange={(e) =>
                            updateEducation(
                              index,
                              "institute_name",
                              e.target.value
                            )
                          }
                          disabled={!isEditing}
                          className="w-full text-[#24786D] border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                          placeholder="Institute Name"
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <input
                            type="text"
                            value={edu.year}
                            onChange={(e) =>
                              updateEducation(index, "year", e.target.value)
                            }
                            disabled={!isEditing}
                            className="text-gray-600 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                            placeholder="Year"
                          />
                          <input
                            type="text"
                            value={edu.gpa}
                            onChange={(e) =>
                              updateEducation(index, "gpa", e.target.value)
                            }
                            disabled={!isEditing}
                            className="text-gray-600 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                            placeholder="GPA"
                          />
                        </div>
                        <input
                          type="text"
                          value={edu.location}
                          onChange={(e) =>
                            updateEducation(index, "location", e.target.value)
                          }
                          disabled={!isEditing}
                          className="w-full text-gray-600 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                          placeholder="Location"
                        />
                      </div>
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => removeEducation(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Certificates Section */}
              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#24786D] mb-4">
                  Certificates
                </h2>
                {isEditing && (
                  <button
                    type="button"
                    onClick={addCertificate}
                    className="mb-4 flex items-center gap-2 text-[#24786D] hover:text-[#1a5a52] transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Certificate
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.certificates.map((cert, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-6">
                      <div className="flex justify-between">
                        <div className="flex-1 space-y-4">
                          <input
                            type="text"
                            value={cert.certificate_name}
                            onChange={(e) =>
                              updateCertificate(
                                index,
                                "certificate_name",
                                e.target.value
                              )
                            }
                            disabled={!isEditing}
                            className="w-full text-lg font-semibold text-gray-800 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                            placeholder="Certificate Name"
                          />
                          <input
                            type="text"
                            value={cert.issuer}
                            onChange={(e) =>
                              updateCertificate(index, "issuer", e.target.value)
                            }
                            disabled={!isEditing}
                            className="w-full text-[#24786D] border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                            placeholder="Issuer"
                          />
                          <input
                            type="text"
                            value={cert.year}
                            onChange={(e) =>
                              updateCertificate(index, "year", e.target.value)
                            }
                            disabled={!isEditing}
                            className="w-full text-gray-600 border-b border-gray-300 bg-transparent focus:outline-none focus:border-[#24786D]"
                            placeholder="Year"
                          />
                        </div>
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => removeCertificate(index)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
